// /simulations/templates/new — the Authoring route for a new Template
// (UI-LAW §4a: authoring that is multi-section and >~12 fields is a Route,
// never a Drawer). A Template is a saved launch spec (docs/14 §5), so this
// route authors it through the same field UI as an ad-hoc launch; there is no
// raw-JSON escape hatch and no way to save the empty spec.
//
// Agent scope is set IN PLACE, exactly as /markers/new and /simulations/new
// do it: the header carries the persistent switcher and the body carries the
// ScopePicker until an agent is chosen. That switcher is the ONLY agent
// mechanism — the old drawer's in-form Agent select is gone, so scope can
// never disagree with the shell.
import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { useTypedNavigate } from "../lib/typedRouter";
import { useWorkingScope, type WorkingScopeSearch } from "../lib/workingScope";
import {
  Button,
  EmptyState,
  Page,
  PageHeader,
  Skeleton,
  useToast,
} from "../ui";
import { AgentScopePicker, AgentScopeSelect } from "./AgentScopeSelect";
import {
  initialLaunchState,
  toTemplateSpec,
  type SimulationLaunchState,
} from "./simulations/simulationLaunchState";
import { useLaunchOptions } from "./simulations/useLaunchOptions";
import { TemplateAuthoringForm } from "./templates/TemplateAuthoringForm";
import { useBatchSpecPreview } from "./simulations/useBatchSpecPreview";

type TemplateCreateBody = components["schemas"]["TemplateCreate"];
type TemplatesSearchState = { tab?: "templates" } & WorkingScopeSearch;

export function TemplateCreate() {
  const { agentId } = useWorkingScope();
  const navigate = useTypedNavigate<TemplatesSearchState>();

  return (
    <Page variant="config">
      {agentId === undefined ? (
        <PageHeader leading={<AgentScopeSelect />} />
      ) : null}
      {agentId === undefined ? (
        <AgentScopePicker
          emptyState={
            <EmptyState
              action={
                <Button
                  onClick={() => {
                    void navigate({ search: true, to: "/agents" });
                  }}
                >
                  Create agent
                </Button>
              }
              description="A template saves one agent's launch spec."
              title="No agents"
            />
          }
          surfaceIcon="play"
          surfaceLabel="Simulations"
        />
      ) : (
        <TemplateCreateForAgent
          agentId={agentId}
          // Remount on scope change: every option in the spec is an
          // agent-scoped id, so in-progress authoring must not survive an
          // agent switch (the MarkerCreate rule).
          key={agentId}
        />
      )}
    </Page>
  );
}

function TemplateCreateForAgent({ agentId }: { agentId: string }) {
  const navigate = useTypedNavigate<TemplatesSearchState>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const launchOptions = useLaunchOptions(agentId);
  const [state, setState] = useState<SimulationLaunchState>(() =>
    initialLaunchState(""),
  );
  const [schedule, setSchedule] = useState("");
  const [scheduleTimezone, setScheduleTimezone] = useState("UTC");
  const preview = useBatchSpecPreview(
    state,
    launchOptions.variables,
    !launchOptions.isPending &&
      !launchOptions.isRefreshingVariables &&
      launchOptions.errorMessage === undefined,
  );

  const returnToTemplates = () => {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({
        agent,
        tab: "templates" as const,
        team,
      }),
      to: "/simulations",
    });
  };

  const createTemplate = useMutation({
    mutationFn: async () => {
      const body: TemplateCreateBody = {
        agent_id: agentId,
        display_name: state.displayName.trim(),
        schedule: schedule.trim().length > 0 ? schedule.trim() : null,
        schedule_timezone: scheduleTimezone.trim(),
        spec: toTemplateSpec(state, launchOptions.variables),
      };
      const { data, error } = await apiClient.POST("/v1/templates", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (template) => {
      queryClient.setQueryData(
        queryKeys.templates.detail(template.id),
        template,
      );
      toast.success("Template saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.templates.all,
      });
      returnToTemplates();
    },
    onError: (error: unknown) => {
      toast.error("Template save failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  // The form owns the header once loaded (its actions must sit inside the
  // form); until then the route keeps the agent switcher on screen.
  if (launchOptions.isPending) {
    return (
      <>
        <PageHeader leading={<AgentScopeSelect />} />
        <Skeleton
          label="Loading template options"
          rows={8}
          shape="detail-field"
        />
      </>
    );
  }
  return (
    <TemplateAuthoringForm
      agentId={agentId}
      errorMessage={
        launchOptions.errorMessage ??
        (createTemplate.isError
          ? requestErrorDescription(createTemplate.error)
          : undefined)
      }
      headerLeading={<AgentScopeSelect />}
      isPreviewPending={
        preview.isPending || launchOptions.isRefreshingVariables
      }
      onRetryOptions={
        launchOptions.errorMessage ? launchOptions.retry : undefined
      }
      isSaving={createTemplate.isPending}
      onCancel={returnToTemplates}
      onChange={setState}
      onSave={() => {
        createTemplate.mutate();
      }}
      onScheduleChange={setSchedule}
      onScheduleTimezoneChange={setScheduleTimezone}
      options={launchOptions.options}
      preview={preview.preview}
      previewErrorMessage={preview.errorMessage}
      onRetryPreview={preview.retry}
      schedule={schedule}
      scheduleTimezone={scheduleTimezone}
      state={state}
      submitLabel="Save template"
      variables={launchOptions.variables}
    />
  );
}
